// TMDB API Service
// Fetches movie metadata (year, genres, ratings, etc.) from The Movie Database

const TMDB_API_KEY = import.meta.env.VITE_TMDB_API_KEY;
const TMDB_BASE_URL = 'https://api.themoviedb.org/3';

export const tmdbService = {
  /**
   * Search for a movie by title
   * @param {string} title - Movie title to search for
   * @param {number} year - Optional year to narrow search
   * @param {number} resultIndex - Which result to return (default 0 for first)
   * @returns {Promise<Object|null>} Movie data or null if not found
   */
  async searchMovie(title, year = null, resultIndex = 0) {
    if (!TMDB_API_KEY) {
      console.warn('TMDB API key not configured');
      return null;
    }

    try {
      const params = new URLSearchParams({
        api_key: TMDB_API_KEY,
        query: title,
        include_adult: false,
        language: 'en-US'
      });

      if (year) {
        params.append('year', year);
      }

      const response = await fetch(`${TMDB_BASE_URL}/search/movie?${params}`);

      if (!response.ok) {
        throw new Error(`TMDB API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.results && data.results.length > resultIndex) {
        const movie = data.results[resultIndex];
        return {
          ...this.formatMovieData(movie),
          totalResults: data.results.length,
          currentIndex: resultIndex
        };
      }

      return null;
    } catch (error) {
      console.error('Error searching TMDB:', error);
      return null;
    }
  },

  /**
   * Get detailed movie information by TMDB ID
   * @param {number} tmdbId - TMDB movie ID
   * @returns {Promise<Object|null>} Detailed movie data
   */
  async getMovieDetails(tmdbId) {
    if (!TMDB_API_KEY) {
      console.warn('TMDB API key not configured');
      return null;
    }

    try {
      const params = new URLSearchParams({
        api_key: TMDB_API_KEY,
        language: 'en-US',
        append_to_response: 'credits,external_ids'
      });

      const response = await fetch(`${TMDB_BASE_URL}/movie/${tmdbId}?${params}`);

      if (!response.ok) {
        throw new Error(`TMDB API error: ${response.status}`);
      }

      const data = await response.json();
      return this.formatMovieData(data, true);
    } catch (error) {
      console.error('Error fetching TMDB details:', error);
      return null;
    }
  },

  /**
   * Format TMDB data into our schema
   * @param {Object} tmdbMovie - Raw TMDB movie object
   * @param {boolean} isDetailed - Whether this is from getMovieDetails
   * @returns {Object} Formatted movie data
   */
  formatMovieData(tmdbMovie, isDetailed = false) {
    const releaseYear = tmdbMovie.release_date
      ? parseInt(tmdbMovie.release_date.split('-')[0])
      : null;

    const formatted = {
      title: tmdbMovie.title,
      releaseYear,
      description: tmdbMovie.overview,
      genre: tmdbMovie.genres
        ? tmdbMovie.genres.map(g => g.name).join(', ')
        : tmdbMovie.genre_ids?.map(id => this.getGenreName(id)).filter(Boolean).join(', ') || null,
      image: tmdbMovie.poster_path
        ? `https://image.tmdb.org/t/p/w500${tmdbMovie.poster_path}`
        : null,
      tmdbId: tmdbMovie.id,
      tmdbRating: tmdbMovie.vote_average,
      tmdbVoteCount: tmdbMovie.vote_count,
      popularity: tmdbMovie.popularity
    };

    if (isDetailed) {
      // Add additional fields from detailed response
      formatted.director = tmdbMovie.credits?.crew
        ?.find(person => person.job === 'Director')
        ?.name || null;

      formatted.runtime = tmdbMovie.runtime ? `${tmdbMovie.runtime} min` : null;

      formatted.imdbId = tmdbMovie.external_ids?.imdb_id || null;
    }

    return formatted;
  },

  /**
   * Map TMDB genre IDs to names
   * @param {number} genreId - TMDB genre ID
   * @returns {string|null} Genre name
   */
  getGenreName(genreId) {
    const genreMap = {
      28: 'Action',
      12: 'Adventure',
      16: 'Animation',
      35: 'Comedy',
      80: 'Crime',
      99: 'Documentary',
      18: 'Drama',
      10751: 'Family',
      14: 'Fantasy',
      36: 'History',
      27: 'Horror',
      10402: 'Music',
      9648: 'Mystery',
      10749: 'Romance',
      878: 'Science Fiction',
      10770: 'TV Movie',
      53: 'Thriller',
      10752: 'War',
      37: 'Western'
    };

    return genreMap[genreId] || null;
  },

  /**
   * Find or enrich movie data
   * @param {string} title - Movie title
   * @param {number} year - Optional year
   * @param {number} resultIndex - Which search result to return
   * @returns {Promise<Object>} Movie data with year and metadata
   */
  async findOrEnrichMovie(title, year = null, resultIndex = 0) {
    try {
      const movieData = await this.searchMovie(title, year, resultIndex);

      if (!movieData) {
        // If TMDB search fails, return minimal data
        return {
          title,
          releaseYear: year || null,
          tmdbFound: false
        };
      }

      return {
        ...movieData,
        tmdbFound: true
      };
    } catch (error) {
      console.error('Error in findOrEnrichMovie:', error);
      return {
        title,
        releaseYear: year || null,
        tmdbFound: false
      };
    }
  }
};
